import React, { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import {
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlineCheck,
  HiOutlineUpload,
  HiOutlineDocumentText,
  HiOutlineUser,
  HiOutlineOfficeBuilding,
  HiOutlineCheckCircle,
} from "react-icons/hi";

const STEPS = [
  { id: 1, label: "Basic Info", icon: HiOutlineUser },
  { id: 2, label: "Shop Details", icon: HiOutlineOfficeBuilding },
];

const ShopCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [avatar, setAvatar] = useState(null);

  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [documents, setDocuments] = useState([]);

  const handleDocumentChange = (e) =>
    setDocuments((prev) => [...prev, ...Array.from(e.target.files)]);
  const removeDocument = (i) =>
    setDocuments((prev) => prev.filter((_, idx) => idx !== i));

  const validate = (s) => {
    if (s === 1) {
      if (!name.trim()) { toast.error("Shop name is required"); return false; }
      if (!email.trim()) { toast.error("Email is required"); return false; }
      if (!phoneNumber.trim()) { toast.error("Phone number is required"); return false; }
      if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return false; }
    }
    if (s === 2) {
      if (!address.trim()) { toast.error("Address is required"); return false; }
      if (!zipCode.trim()) { toast.error("ZIP / PIN code is required"); return false; }
    }
    return true;
  };

  const handleNext = () => { if (validate(step)) setStep((s) => s + 1); };
  const handleBack = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!validate(2)) return;
    setIsLoading(true);
    try {
      const fd = new FormData();
      if (avatar) fd.append("file", avatar);
      fd.append("name", name);
      fd.append("email", email);
      fd.append("password", password);
      fd.append("phoneNumber", phoneNumber);
      fd.append("address", address);
      fd.append("zipCode", zipCode);
      documents.forEach((f) => fd.append("tradeLicenses", f));
      const res = await axios.post(`${server}/shop/create-shop`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-between p-14">
          <Link to="/"><img src="/logo (10).png" alt="Logo" className="h-10 w-auto invert brightness-200 object-contain" /></Link>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">Application Submitted</p>
            <h1 className="text-4xl font-bold text-white leading-snug mb-6">Thank you for<br />joining us!</h1>
            <p className="text-secondary-400 text-sm leading-relaxed max-w-sm">Check your email to activate your account, then wait for admin approval to access your vendor dashboard.</p>
          </div>
          <p className="text-secondary-600 text-xs">&#169; {new Date().getFullYear()} Positive Moments</p>
        </div>
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-black flex items-center justify-center mx-auto mb-6">
              <HiOutlineCheckCircle className="w-10 h-10 text-accent-500" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Success</p>
            <h2 className="text-3xl font-bold text-black mb-4">Application Submitted!</h2>
            <p className="text-secondary-500 text-sm mb-8 leading-relaxed">Check your email to verify your account. Once confirmed, your application will be reviewed. You will be notified once approved.</p>
            <div className="space-y-3 text-left bg-secondary-50 border border-secondary-200 p-6 mb-8">
              {["Email verification sent","Admin reviews your application","Approval notification via email","Access your vendor dashboard"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-accent-500 flex items-center justify-center flex-shrink-0"><HiOutlineCheck className="w-3 h-3 text-black" /></div>
                  <span className="text-sm text-secondary-600">{item}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/shop-login")} className="w-full py-3.5 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors">Go to Vendor Login</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-5/12 bg-black flex-col justify-between p-14 sticky top-0 h-screen">
        <Link to="/"><img src="/logo (10).png" alt="Logo" className="h-10 w-auto invert brightness-200 object-contain" /></Link>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">Vendor Registration</p>
          <h1 className="text-4xl font-bold text-white leading-snug mb-6">Sell with<br /><span className="text-accent-500">Positive Moments.</span></h1>
          <p className="text-secondary-400 text-sm leading-relaxed max-w-sm mb-10">Join India's growing wellness marketplace and bring your products to thousands of conscious consumers.</p>
          <div className="space-y-4">
            {STEPS.map((s) => (
              <div key={s.id} className="flex items-center gap-4">
                <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors ${step > s.id ? "bg-accent-500" : step === s.id ? "bg-white" : "bg-secondary-800 border border-secondary-700"}`}>
                  {step > s.id ? <HiOutlineCheck className="w-4 h-4 text-black" /> : <s.icon className={`w-4 h-4 ${step === s.id ? "text-black" : "text-secondary-500"}`} />}
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-wider font-semibold ${step === s.id ? "text-white" : step > s.id ? "text-accent-400" : "text-secondary-600"}`}>Step {s.id}</p>
                  <p className={`text-sm ${step === s.id ? "text-white font-medium" : step > s.id ? "text-secondary-400" : "text-secondary-700"}`}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-secondary-600 text-xs">&#169; {new Date().getFullYear()} Positive Moments</p>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-12 overflow-y-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-black transition-colors mb-8 lg:hidden">
          <HiOutlineArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="lg:hidden flex justify-center mb-8"><img src="/logo (10).png" alt="Logo" className="h-10 w-auto object-contain" /></div>
        <div className="lg:hidden flex items-center gap-1 mb-8">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`flex items-center justify-center w-7 h-7 text-xs font-bold flex-shrink-0 ${step > s.id ? "bg-accent-500 text-black" : step === s.id ? "bg-black text-white" : "bg-secondary-100 text-secondary-400"}`}>
                {step > s.id ? <HiOutlineCheck className="w-3.5 h-3.5" /> : s.id}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.id ? "bg-accent-500" : "bg-secondary-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="w-full max-w-lg mx-auto">
          {step === 1 && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Step 1 of 2</p>
              <h2 className="text-3xl font-bold text-black mb-2">Basic Information</h2>
              <p className="text-secondary-500 text-sm mb-8">Tell us about yourself and your shop.</p>
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-3">Shop Logo <span className="text-secondary-400 normal-case tracking-normal font-normal">(Optional)</span></label>
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 border-2 border-dashed border-secondary-300 flex items-center justify-center overflow-hidden bg-secondary-50">
                    {avatar ? <img src={URL.createObjectURL(avatar)} alt="avatar" className="w-full h-full object-cover" /> : <HiOutlineUser className="w-7 h-7 text-secondary-300" />}
                  </div>
                  <label htmlFor="avatar-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 border border-black text-black text-xs font-semibold uppercase tracking-wider hover:bg-black hover:text-white transition-colors">
                    <HiOutlineUpload className="w-4 h-4" /> Upload Logo
                    <input id="avatar-upload" type="file" accept="image/*" onChange={(e) => setAvatar(e.target.files[0])} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Shop / Business Name *</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Herbal Roots India" className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Email Address *</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Phone Number *</label>
                  <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+91 98765 43210" className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Password *</label>
                  <div className="relative">
                    <input type={visible ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" className="w-full px-4 py-3 pr-12 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                    <button type="button" onClick={() => setVisible(!visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-black transition-colors">
                      {visible ? <AiOutlineEye size={18} /> : <AiOutlineEyeInvisible size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Step 2 of 2</p>
              <h2 className="text-3xl font-bold text-black mb-2">Shop Details</h2>
              <p className="text-secondary-500 text-sm mb-8">Provide your shop address and any supporting documents.</p>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Business Address *</label>
                  <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, City, State" className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">PIN / ZIP Code *</label>
                  <input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="560001" className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Business Documents <span className="text-secondary-400 normal-case tracking-normal font-normal">(Optional)</span></label>
                  <div className="border-2 border-dashed border-secondary-300 p-6 text-center hover:border-black transition-colors">
                    <HiOutlineUpload className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
                    <p className="text-sm text-secondary-600 mb-1">Upload trade license or business proof</p>
                    <p className="text-xs text-secondary-400 mb-4">PDF, JPG, PNG accepted</p>
                    <label htmlFor="doc-upload" className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white text-xs font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors cursor-pointer">
                      <HiOutlineUpload className="w-4 h-4" /> Choose Files
                      <input id="doc-upload" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocumentChange} className="hidden" />
                    </label>
                  </div>
                  {documents.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {documents.map((f, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 bg-secondary-50 border border-secondary-200">
                          <div className="flex items-center gap-2 min-w-0">
                            <HiOutlineDocumentText className="w-4 h-4 text-accent-600 flex-shrink-0" />
                            <span className="text-xs text-secondary-700 truncate">{f.name}</span>
                          </div>
                          <button type="button" onClick={() => removeDocument(i)} className="text-secondary-400 hover:text-black transition-colors ml-2 text-xs">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={`flex gap-3 mt-8 pt-8 border-t border-secondary-100 ${step > 1 ? "justify-between" : "justify-end"}`}>
            {step > 1 && (
              <button type="button" onClick={handleBack} disabled={isLoading} className="inline-flex items-center gap-2 px-6 py-3 border border-secondary-300 text-secondary-600 text-sm font-semibold uppercase tracking-wider hover:border-black hover:text-black transition-colors disabled:opacity-50">
                <HiOutlineArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {step < 2 && (
              <button type="button" onClick={handleNext} className="inline-flex items-center gap-2 px-8 py-3 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors ml-auto">
                Continue <HiOutlineArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 2 && (
              <button type="button" onClick={handleSubmit} disabled={isLoading} className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 text-black text-sm font-semibold uppercase tracking-wider hover:bg-accent-400 transition-colors disabled:opacity-50 ml-auto">
                {isLoading ? (<><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg> Submitting...</>) : (<>Submit Application <HiOutlineCheck className="w-4 h-4" /></>)}
              </button>
            )}
          </div>
          <p className="text-center text-xs text-secondary-400 mt-6">Already registered? <Link to="/shop-login" className="text-black font-semibold underline underline-offset-2 hover:text-accent-600 transition-colors">Sign in here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ShopCreate;
